import { Box, styled } from '@mui/material';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

import type {
  ChkHeaderProps,
  TextHandlers,
  TextProps,
} from './ChkHeader.types';

export const ChkHeader = forwardRef<HTMLDivElement, ChkHeaderProps>(
  ({ componentStart, componentEnd, children, ...rest }, ref) => (
    <MainWrapper {...rest}>
      <ComponentWrapper sx={{ marginRight: 'auto' }}>
        {componentStart}
      </ComponentWrapper>
      <Text ref={ref}>{children}</Text>
      <ComponentWrapper sx={{ marginLeft: 'auto' }}>
        {componentEnd}
      </ComponentWrapper>
    </MainWrapper>
  )
);

const MainWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  textAlign: 'center',
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  minHeight: theme.zoom.isZoomed ? 'unset' : '5.2rem',
  color: theme.tokens.navigationHeader,
  width: '100%',
  ...theme.typography.h3,
  fontWeight: theme.typography.fontWeightMedium,
  flexWrap: theme.zoom.isZoomed ? 'wrap-reverse' : 'nowrap',
}));

const ComponentWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: 'auto',
  minHeight: theme.spacing(7),
}));

const Text = forwardRef<TextHandlers, TextProps>(({ children }, ref) => {
  const innerRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => {
    return {
      focus() {
        innerRef.current?.focus();
      },
    };
  }, []);

  useEffect(() => {
    innerRef.current?.focus();
  }, []);

  if (!children) return null;

  return (
    <div
      role="heading"
      aria-level={1}
      tabIndex={-1}
      ref={innerRef}
      style={{ outline: 'none', wordBreak: 'break-word' }}
    >
      {children}
    </div>
  );
});
